import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useCallback, useState } from "react"
import { useDropzone } from "react-dropzone"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import Row from "../../../../../common/components/Row"
import DialogHeader from "../../../components/DialogHeader"
import PaperIcon from "../../../components/PaperIcon"
import { useDialogContainer } from "../../../utils"

export default function UploadFileDialog({ open, onClose, candidate }) {
  const [isUploading, setIsUploading] = useState(false)
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function handleSoftClose() {
    onClose()
  }

  const handleFileDrop = useCallback(
    (accptedFiles) => {
      accptedFiles.forEach((file) => {
        const fileReader = new FileReader()
        fileReader.onload = () => {
          setIsUploading(true)
          const formData = new FormData()
          formData.append(
            "file",
            new Blob([fileReader.result], { type: file.type }),
            file.name
          )
          axios
            .post(`/crm/api/file-upload/${candidate.id}/`, formData)
            .then(({ data }) => onClose(data))
            .finally(() => setIsUploading(false))
        }
        fileReader.readAsArrayBuffer(file)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: selectFile,
  } = useDropzone({ onDrop: handleFileDrop })

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      sx={{
        "& > .MuiPaper-root": {
          width: "400px",
        },
      }}
    >
      <DialogHeader title="Add File" onClose={handleSoftClose} />
      <DialogContent>
        <FileUploadPaper
          elevation={0}
          {...getRootProps()}
          sx={(theme) => ({
            outlineColor: isDragActive
              ? theme.palette.colors.brand500
              : theme.palette.colors.gray200,
            outlineWidth: isDragActive ? "1.5px" : "1px",
          })}
        >
          <PaperIcon>
            <CloudUploadOutlinedIcon />
          </PaperIcon>
          <Row sx={{ alignItems: "center", gap: 0.5 }}>
            <Link
              href="#"
              color="primary.main"
              variant="body1"
              sx={{ fontWeight: 600 }}
            >
              Click to upload
            </Link>
            <Typography variant="body1" color="text.secondary">
              or drag and drop
            </Typography>
          </Row>
          <Typography variant="body2" color="text.secondary" lineHeight={1}>
            Max file size: 10MB
          </Typography>
          <input {...getInputProps()} />
        </FileUploadPaper>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            opacity: 0.6,
            fontSize: "0.75rem",
          }}
        >
          Note: Drag and drop will automatically upload the file.
        </Typography>
        <Box>
          <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
            Close
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isUploading}
            onClick={() => selectFile()}
            sx={{ display: "none" }}
          >
            Add
          </LoadingButton>
        </Box>
      </DialogActions>
    </Container>
  )
}

const FileUploadPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  paddingInline: theme.spacing(3),
  paddingBlock: theme.spacing(2),
  gap: theme.spacing(1),
  transition: theme.transitions.create(["outline-color", "border-width"]),
}))
